<script>
    import Vue3Autocounter from 'vue3-autocounter';

    import constants from '../../constants.js';

    export default {
        components: {
            Vue3Autocounter,
        },
        props: {
            quizItems: {
                type: Array,
                required: true,
            },
            stepSentenceLabel: {
                type: String,
                default: 'Quiz Step [step] of [stepAmount]',
            },
            rightLabel: {
                type: String,
                default: 'Correct!',
            },
            wrongLabel: {
                type: String,
                default: 'Incorrect!',
            },
            checkAnswersLabel: {
                type: String,
                default: 'Check Answer(s)',
            },
            continueLabel: {
                type: String,
                default: 'Continue',
            },
            singleAnswerIntroLabel: {
                type: String,
                default: 'Select the correct answer',
            },
            singleAnswerCorrectLabel: {
                type: String,
                default: 'Well done, you chose the right answer!',
            },
            singleAnswerIncorrectLabel: {
                type: String,
                default: 'Too bad... This is not the right answer. The correct answer is',
            },
            multipleAnswerIntroLabel: {
                type: String,
                default: 'Select all the correct answers',
            },
            multipleAnswerCorrectLabel: {
                type: String,
                default: 'Well done, all answers are correct!',
            },
            multipleAnswerIncorrectLabel: {
                type: String,
                default: 'Nice try! Unfortunately, these are not the correct answers. The correct answers are',
            },
            combinationIntroLabel: {
                type: String,
                default: 'Match the questions with the correct answers',
            },
            combinationCorrectLabel: {
                type: String,
                default: 'Well done, all combinations are correct!',
            },
            combinationIncorrectLabel: {
                type: String,
                default: 'Nice try! Unfortunately, not all combinations are correct. The correct combinations are',
            },
            dropLabel: {
                type: String,
                default: 'Drop here',
            },
        },
        data() {
            return {
                constants,

                currentStep: 0,
                selectedAnswers: [],
                selectedAnswer: null,
                showAnswers: false,
                isCorrect: false,
                isPristine: true,
                draggedItem: null,
                questions: [],
                answers: [],

                userAnswersTrue: [],
                ultimateScore: 0,
            };
        },
        computed: {
            stepSentenceLabelComputed() {
                return this.stepSentenceLabel.replace(/\[step\]/g, this.currentStep + 1).replace(/\[stepAmount\]/g, this.quizItems.length);
            },
            currentItem() {
                if (!this.quizItems.length) return null;
                return this.quizItems[this.currentStep];
            },
            currentItemIsQuestion() {
                if (!this.currentItem) return false;
                return this.currentItem.Type === this.constants.quiztypes.checkbox || this.currentItem.Type === this.constants.quiztypes.radio || this.currentItem.Type === this.constants.quiztypes.draggable;
            },
            userScore() {
                if (!this.userAnswersTrue.length) return 1;
                return Math.max(1, Math.min(10, Math.round((this.userAnswersTrue.map(a => a.value).filter(Boolean).length / this.userAnswersTrue.length) * 10)));
            },
            showContinueButton() {
                if (!this.quizItems.length) return false;
                return this.currentStep + 1 != this.quizItems.length;
            },
        },
        watch: {
            currentStep() {
                this.initializeSliderCombination();
                this.setUltimateScore();
            },
        },
        methods: {
            nextStep() {
                if (this.currentStep < this.quizItems.length - 1) {
                    this.currentStep++;
                    this.resetStepState();

                    if (this.$refs.top) this.$refs.top.scrollIntoView({ behavior: "smooth" });
                }
            },
            initializeSliderCombination() {
                if (this.currentItem.Type === this.constants.quiztypes.draggable) {
                    this.questions = this.currentItem.ComparisonQuestion.LeftItems;
                    this.answers = (this.currentItem.ComparisonQuestion.RightItems || []).map((item) => ({
                        ...item,
                        matched: null,
                    }));
                }
            },
            setUltimateScore() {
                if (this.currentItem.Type === this.constants.quiztypes.score) {
                    const duration = 2000;
                    const difference = this.userScore - this.ultimateScore;

                    const incrementSpeed = difference / (duration / 10);
                    setTimeout(() => {
                        const interval = setInterval(() => {
                            this.ultimateScore += incrementSpeed;

                            if ((difference > 0 && this.ultimateScore >= this.userScore) || (difference < 0 && this.ultimateScore <= this.userScore)) {
                                this.ultimateScore = this.userScore;
                                clearInterval(interval);
                            }
                        }, 10);
                    }, 500);
                }
            },
            onDragStart(question) {
                if (!question) return;
                this.draggedItem = question;
            },
            onDrop(target) {
                if (this.draggedItem) {
                    this.isPristine = false;
                    const answer = this.answers.filter(a => a.matched).find(a => a.matched.ComparisonID === this.draggedItem.ComparisonID);
                    if (answer) {
                        this.answers = this.answers.map((a) => {
                            if (a.ComparisonID === answer.ComparisonID && answer.ComparisonID !== target.ComparisonID) return { ...a, matched: null };
                            return a;
                        });
                    }
                    target.matched = { ...this.draggedItem };
                }
                this.draggedItem = null;
            },
            checkAnswer(item) {
                switch (item.Type) {
                    case this.constants.quiztypes.radio:
                        this.checkSingleAnswer();
                        break;
                    case this.constants.quiztypes.checkbox:
                        this.checkMultipleAnswers();
                        break;
                    case this.constants.quiztypes.draggable:
                        this.checkSliderAnswers();
                        break;
                    default:
                        break;
                }
            },
            checkSingleAnswer() {
                const currentItem = this.quizItems[this.currentStep];
                const correctAnswer = currentItem.Answers.find((answer) => answer.IsCorrect)?.AnswerText;

                this.isCorrect = this.selectedAnswer === correctAnswer;
                this.finishQuestion();
            },
            checkMultipleAnswers() {
                const currentItem = this.quizItems[this.currentStep];
                const correctAnswers = currentItem.Answers.filter((answer) => answer.IsCorrect).map((answer) => answer.AnswerText);

                const allCorrectSelected = correctAnswers.every((answer) => this.selectedAnswers.includes(answer));
                const anyIncorrectSelected = this.selectedAnswers.some((answer) => !correctAnswers.includes(answer));

                this.isCorrect = allCorrectSelected && !anyIncorrectSelected && this.selectedAnswers.length === correctAnswers.length;
                this.finishQuestion();
            },
            checkSliderAnswers() {
                const allCorrect = this.answers.every((answer) => answer.matched && answer.matched.ComparisonID === answer.ComparisonID);

                this.isCorrect = allCorrect;
                this.finishQuestion();
            },
            checkSingleCombination(question) {
                const answer = this.answers.filter(a => a.matched).find(a => a.matched.ComparisonID === question.ComparisonID);
                return answer && answer.ComparisonID === question.ComparisonID;
            },
            checkQuestionDragged(question) {
                const answer = this.answers.filter(a => a.matched).find(a => a.matched.ComparisonID === question.ComparisonID);
                return answer;
            },
            finishQuestion() {
                this.userAnswersTrue = this.userAnswersTrue.map((item) => item.index === this.currentStep ? { ...item, value: this.isCorrect } : item);
                this.showAnswers = true;
            },
            resetStepState() {
                this.selectedAnswers = [];
                this.selectedAnswer = null;
                this.showAnswers = false;
                this.isCorrect = false;
                this.questions = [];
                this.answers = [];
                this.isPristine = true;
            },
        },
        mounted() {
            this.userAnswersTrue = this.quizItems
                .filter(item => item.Type != this.constants.quiztypes.text && item.Type != this.constants.quiztypes.score)
                .map(item => {
                    const index = this.quizItems.indexOf(item);

                    return {
                        index,
                        value: false
                    }
                });
            this.setUltimateScore();
            this.initializeSliderCombination();
        },
    };
</script>

<template>
    <div ref="top" class="widget-quiz__top content">
        <p>{{ stepSentenceLabelComputed }}</p>
    </div>
    <progress class="progress is-primary" :value="currentStep + 1" :max="quizItems.length"></progress>

    <template v-if="currentItem">
        <template v-if="currentItemIsQuestion">
            <h4 class="subtitle is-4">{{ currentItem.Question }}</h4>

            <template v-if="currentItem.Type === constants.quiztypes.radio">
                <div class="content">
                    <p>{{ singleAnswerIntroLabel }}</p>
                </div>
                <ul>
                    <li v-for="(answer, i) in currentItem.Answers" :key="i" class="is-flex is-align-items-center">
                        <span class="icon mr-3" v-if="showAnswers">
                            <i v-if="answer.IsCorrect" class="fa-solid fa-circle-check fa-md has-text-success m-0"></i>
                            <i v-else class="fa-solid fa-circle-xmark fa-md has-text-danger m-0"></i>
                        </span>
                        <div class="field">
                            <fieldset>
                                <div class="control">
                                    <input type="radio" :id="'radio-' + i" :value="answer.AnswerText" v-model="selectedAnswer" class="by-radio" @change="isPristine = false" />
                                    <label :for="'radio-' + i" class="by-checkable pl-10">{{ answer.AnswerText }}</label>
                                </div>
                            </fieldset>
                        </div>
                    </li>
                </ul>

                <div v-if="showAnswers" class="box content has-background-blue-light mt-5 mb-0 p-9">
                    <template v-if="isCorrect">
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-check fa-2xl has-text-success"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ correctLabel }}</p>
                        </div>
                        <p class="has-text-centered">{{ singleAnswerCorrectLabel }}</p>
                    </template>
                    <template v-else>
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-xmark fa-2xl has-text-danger"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ incorrectLabel }}</p>
                        </div>
                        <p class="has-text-centered">
                            {{ singleAnswerIncorrectLabel }}
                            <span v-for="(answer, i) in currentItem.Answers.filter((a) => a.IsCorrect)" :key="'correct-' + i">
                                {{ answer.AnswerText }}
                            </span>
                        </p>
                    </template>
                </div>
            </template>

            <template v-else-if="currentItem.Type === constants.quiztypes.checkbox">
                <div class="content">
                    <p>{{ multipleAnswerIntroLabel }}</p>
                </div>
                <ul>
                    <li v-for="(answer, i) in currentItem.Answers" :key="i" class="is-flex is-align-items-center">
                        <span class="icon mr-3" v-if="showAnswers">
                            <i v-if="answer.IsCorrect" class="fa-solid fa-circle-check fa-md has-text-success m-0"></i>
                            <i v-else class="fa-solid fa-circle-xmark fa-md has-text-danger m-0"></i>
                        </span>
                        <div class="field">
                            <fieldset>
                                <div class="control">
                                    <input type="checkbox" :id="'checkbox-' + i" :value="answer.AnswerText" v-model="selectedAnswers" class="by-checkbox" @change="isPristine = false" />
                                    <label :for="'checkbox-' + i" class="by-checkable pl-10">{{ answer.AnswerText }}</label>
                                </div>
                            </fieldset>
                        </div>
                    </li>
                </ul>

                <div v-if="showAnswers" class="box content has-background-blue-light mt-5 mb-0 p-9">
                    <template v-if="isCorrect">
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-check fa-2xl has-text-success"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ correctLabel }}</p>
                        </div>
                        <p class="has-text-centered">{{ multipleAnswerCorrectLabel }}</p>
                    </template>
                    <template v-else>
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-xmark fa-2xl has-text-danger"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ incorrectLabel }}</p>
                        </div>
                        <p class="has-text-centered">{{ multipleAnswerIncorrectLabel }}</p>
                        <ul>
                            <li v-for="(answer, i) in currentItem.Answers.filter((a) => a.IsCorrect)" :key="'correct-' + i">
                                {{ answer.AnswerText }}
                            </li>
                        </ul>
                    </template>
                </div>
            </template>

            <template v-else-if="currentItem.Type === constants.quiztypes.draggable">
                <div class="content">
                    <p>{{ combinationIntroLabel }}</p>
                </div>

                <div class="drag-container">
                    <div class="drag-column">
                        <ul>
                            <li v-for="(question, i) in questions" :key="'question-' + i" class="is-flex is-align-items-center">
                                <span class="icon mr-3" v-if="showAnswers">
                                    <i v-if="checkSingleCombination(question)" class="fa-solid fa-circle-check fa-md has-text-success m-0"></i>
                                    <i v-else class="fa-solid fa-circle-xmark fa-md has-text-danger m-0"></i>
                                </span>
                                <div :class="['drag-item', 'drag-item--draggable', 'is-flex-grow-1', { 'drag-item--draggable-dragged': checkQuestionDragged(question) }]" draggable="true" @dragstart="onDragStart(question)">
                                    {{ question.AnswerText }}
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div class="drag-column drag-column--drop">
                        <ul>
                            <li v-for="(answer, i) in answers" :key="'drop-' + i" class="drag-item drag-item--droppable" @dragover.prevent @drop="onDrop(answer)">
                                <span v-if="answer.matched">{{ answer.matched.AnswerText }}</span>
                                <span v-else class="drag-item__placeholder">{{ dropLabel }}</span>
                            </li>
                        </ul>
                    </div>
                    <div class="drag-column">
                        <ul>
                            <li v-for="(answer, i) in answers" :key="'answer-' + i" class="drag-item drag-item--answer">
                                {{ answer.AnswerText }}
                            </li>
                        </ul>
                    </div>
                </div>

                <div v-if="showAnswers" class="box content has-background-blue-light mt-5 mb-0 pb-10">
                    <template v-if="isCorrect">
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-check fa-2xl has-text-success"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ correctLabel }}</p>
                        </div>
                        <p class="has-text-centered">{{ combinationCorrectLabel }}</p>
                    </template>
                    <template v-else>
                        <div class="is-flex is-justify-content-center is-flex-direction-column is-align-items-center mb-10">
                            <span class="icon is-large">
                                <i class="fa-solid fa-circle-xmark fa-2xl has-text-danger"></i>
                            </span>
                            <p class="mb-0 has-text-weight-bold">{{ incorrectLabel }}</p>
                        </div>
                        <p class="has-text-centered">{{ combinationIncorrectLabel }}</p>
                        <ul>
                            <li v-for="(question, i) in questions" :key="'combination-' + i">
                                {{ question.AnswerText }} - {{ answers.find(a => a.ComparisonID === question.ComparisonID).AnswerText }}
                            </li>
                        </ul>
                    </template>
                </div>
            </template>

            <div class="buttons mt-9">
                <button class="button is-secondary is-outlined" @click="checkAnswer(currentItem)" :disabled="isPristine">{{ checkAnswersLabel }}</button>
                <button v-if="showContinueButton" class="button is-primary is-outlined" @click="nextStep" :disabled="!showAnswers">{{ continueLabel }}</button>
            </div>
        </template>
        <template v-else>
            <template v-if="currentItem.Type === constants.quiztypes.text">
                <div class="content">
                    <p>{{ currentItem.Text }}</p>
                </div>
                <div class="buttons mt-5">
                    <button @click="nextStep" class="button is-primary is-outlined">{{ currentItem.ButtonText }}</button>
                </div>
            </template>

            <template v-else-if="currentItem.Type === constants.quiztypes.score">
                <h4 class="subtitle is-4">{{ currentItem.Title }}</h4>
                <div class="score-card">
                    <svg class="score-circle" viewBox="0 0 100 100">
                        <circle class="score-background" cx="50" cy="50" r="45"></circle>

                        <circle class="score-progress" cx="50" cy="50" r="45" 
                                :style="{ strokeDashoffset: 282 * (1 - ultimateScore / 10) + 'px' }">
                        </circle>
                    </svg>
                    <div class="score-number">
                        {{ Math.round(ultimateScore) }}
                    </div>
                </div>
                <a v-if="currentItem.Link" :href="currentItem.Link" :target="currentItem.LinkTarget" class="button is-primary">{{ currentItem.LinkName }}</a>
            </template>
        </template>
    </template>
</template>
